import { auth, getMessagingSafe, isNative, VAPID_KEY } from "../config/firebase";
import { getToken, onMessage } from "firebase/messaging";
import { Severity } from "@beyondrealityapp/core/shared/constants";
import { showSnackbar } from "../features/snackbar";
import { useDispatch } from "react-redux";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useFetchUserQuery,
  usePersistPushNotificationTokenMutation,
} from "../api/user";
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from "@capacitor/push-notifications";

// Message type the service worker posts when a notification is tapped
export const NOTIFICATION_CLICK = "notification-click";

type NotificationProviderProps = {
  children: React.ReactNode;
};

const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [persistPushNotificationToken] =
    usePersistPushNotificationTokenMutation();
  const { data: user } = useFetchUserQuery(auth.currentUser?.uid ?? "");

  const handleNavigate = useCallback(
    (link: string) => navigate(link),
    [navigate]
  );

  useEffect(() => {
    // Register for push when any buddy notification or rhythm reminder is on
    const wantsPush =
      Object.values(user?.notifications ?? {}).some((v) => v) ||
      user?.rhythm?.reminders === true;
    if (!user || !wantsPush) return;

    if (isNative) {
      // Native path: use Capacitor PushNotifications plugin (APNs / FCM)
      PushNotifications.requestPermissions().then(({ receive }) => {
        if (receive === "granted") {
          PushNotifications.register();
        }
      });

      const tokenListener = PushNotifications.addListener(
        "registration",
        (token: Token) => {
          persistPushNotificationToken(token.value);
        }
      );
      const errorListener = PushNotifications.addListener(
        "registrationError",
        (error) => {
          console.error("Push registration error:", error);
        }
      );

      return () => {
        tokenListener.then((l) => l.remove());
        errorListener.then((l) => l.remove());
      };
    }

    // Web path: VAPID / Firebase Web Messaging
    let cancelled = false;
    getMessagingSafe().then((messaging) => {
      if (cancelled || !messaging) return;

      getToken(messaging, { vapidKey: VAPID_KEY })
        .then((token) => {
          if (!cancelled) persistPushNotificationToken(token);
        })
        .catch((error) => {
          console.error("Error getting push notification token:", error);
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (isNative) {
      // Native path: foreground notification → snackbar with deep link
      const foregroundListener = PushNotifications.addListener(
        "pushNotificationReceived",
        (notification: PushNotificationSchema) => {
          const link = notification.data?.link as string | undefined;
          const title = notification.title;
          let action = undefined;

          if (link) {
            action = {
              label: "View",
              onClick: () => handleNavigate(link),
            };
          }

          if (title) {
            dispatch(
              showSnackbar({
                message: `New notification: ${title}`,
                severity: Severity.INFO,
                action,
              })
            );
          }
        }
      );

      const tapListener = PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (action: ActionPerformed) => {
          const link = action.notification.data?.link as string | undefined;
          if (link) handleNavigate(link);
        }
      );

      return () => {
        foregroundListener.then((l) => l.remove());
        tapListener.then((l) => l.remove());
      };
    }

    // Web path: Firebase onMessage
    getMessagingSafe().then((messaging) => {
      if (!messaging) return;

      onMessage(messaging, (payload) => {
        let action = undefined;
        const link = payload.fcmOptions?.link;
        const title = payload.notification?.title;

        if (link) {
          action = {
            label: "View",
            onClick: () => handleNavigate(link),
          };
        }

        if (title) {
          dispatch(
            showSnackbar({
              message: `New notification: ${title}`,
              severity: Severity.INFO,
              action: action,
            })
          );
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNavigate]);

  useEffect(() => {
    // A tapped web notification while the app is open: the service worker
    // focuses this window and posts the link (public/firebase-messaging-sw.js)
    if (isNative || !("serviceWorker" in navigator)) return;
    const onWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type !== NOTIFICATION_CLICK) return;
      const url = new URL(String(event.data.link ?? ""), window.location.origin);
      if (url.origin === window.location.origin) handleNavigate(url.pathname + url.search);
    };
    const worker = navigator.serviceWorker;
    worker.addEventListener("message", onWorkerMessage);
    return () => worker.removeEventListener("message", onWorkerMessage);
  }, [handleNavigate]);

  return <>{children}</>;
};

export default NotificationProvider;
